import { capitalCase } from "change-case";
import { useEffect } from "react";

import { loadLocale } from "@smart/itops-locale-dom";
import { defaultFilters } from "@smart/itops-sb-design-system-dom";
import { useProviderInfo } from "@smart/itops-smokeball-components-dom";
import {
  useLoadCompletedSubmissions,
  useLoadPendingSubmissions,
  useLoadWebEnquiries,
} from "@smart/manage-gql-client-dom";

import { Widget } from "./widget";
import { useTimeFilterValue } from "../hooks/time-filter-value";
import { HomeTabKey } from "../utils";

export const SubmissionWidgets = ({
  pendingSubmissions,
  onWidgetClick,
}: {
  pendingSubmissions: ReturnType<typeof useLoadPendingSubmissions>;
  onWidgetClick: (tabKey: HomeTabKey) => void;
}) => {
  const locale = loadLocale();
  const providerInfo = useProviderInfo();
  const isTriConvey = providerInfo?.label === "triConvey";

  const {
    timeFilter: webEnquiriesTimeFilter,
    setTimeFilter: setWebEnquiriesTimeFilter,
  } = useTimeFilterValue({
    filterStorageKey: "intake-home-web-enquiries-filter",
  });
  const {
    timeFilter: completedSubmissionsTimeFilter,
    setTimeFilter: setCompletedSubmissionsTimeFilter,
  } = useTimeFilterValue({
    filterStorageKey: "intake-home-completed-intake-forms-filter",
  });

  const {
    loading: loadingPendingSubmissions,
    result: pendingSubmissionsResult,
    refetching: refetchingPendingSubmissions,
  } = pendingSubmissions;

  const {
    loading: loadingWebEnquiries,
    result: webEnquiries,
    refetching: refetchingWebEnquiries,
    refetch: refetchWebEnquiries,
  } = useLoadWebEnquiries(webEnquiriesTimeFilter, {
    reloadOnVisible: true,
    pollInterval: 180000,
  });

  const {
    loading: loadingCompletedSubmissions,
    result: completedSubmissions,
    refetching: refetchingCompletedSubmissions,
    refetch: refetchCompletedSubmissions,
  } = useLoadCompletedSubmissions(completedSubmissionsTimeFilter, {
    reloadOnVisible: true,
    pollInterval: 180000,
  });

  const actionLinks = {
    AU: {
      enquiries:
        "https://support.smokeball.com/hc/en-au/articles/8015208933911",
      pending: "https://support.smokeball.com/hc/en-au/articles/16264150092183",
      completed:
        "https://support.smokeball.com/hc/en-au/articles/16264150092183",
    },
    US: {
      enquiries:
        "https://support.smokeball.com/hc/en-us/articles/8015208933911",
      pending: "https://support.smokeball.com/hc/en-us/articles/16264150092183",
      completed:
        "https://support.smokeball.com/hc/en-us/articles/16264150092183",
    },
    GB: {
      enquiries:
        "https://support.smokeball.com/hc/en-gb/articles/8015208933911",
      pending: "https://support.smokeball.com/hc/en-gb/articles/16264150092183",
      completed:
        "https://support.smokeball.com/hc/en-gb/articles/16264150092183",
    },
  };

  const triConveyActionLinks = {
    enquiries:
      "https://help.trisearch.com.au/support/solutions/articles/51000404104",
    pending:
      "https://help.trisearch.com.au/support/solutions/articles/51000404117",
    completed:
      "https://help.trisearch.com.au/support/solutions/articles/51000404117",
  };

  const getWebEnquiryActionLink = () => {
    if (loadingWebEnquiries || webEnquiries.length) return undefined;
    if (isTriConvey) {
      return triConveyActionLinks.enquiries;
    }
    return actionLinks[locale.country].enquiries;
  };

  const getPendingIntakeFormActionLink = () => {
    if (loadingPendingSubmissions || pendingSubmissionsResult.length)
      return undefined;
    if (isTriConvey) {
      return triConveyActionLinks.pending;
    }
    return actionLinks[locale.country].pending;
  };

  const getCompletedIntakeFormActionLink = () => {
    if (
      loadingCompletedSubmissions ||
      completedSubmissions.length ||
      pendingSubmissionsResult.some((s) => s.values.status === "active")
    )
      return undefined;
    if (isTriConvey) {
      return triConveyActionLinks.completed;
    }
    return actionLinks[locale.country].completed;
  };
  const filters = defaultFilters.filter((f) => f !== "allTime");

  useEffect(() => {
    if (webEnquiriesTimeFilter) refetchWebEnquiries();
  }, [webEnquiriesTimeFilter]);

  useEffect(() => {
    if (completedSubmissionsTimeFilter) refetchCompletedSubmissions();
  }, [completedSubmissionsTimeFilter]);

  return (
    <div className="flex w-full gap-medium">
      <Widget
        iconName="regularGlobe"
        iconBgColour="bg-pink-150"
        title={`Web ${capitalCase(locale.terms.enquiry.plural)}`}
        submissions={webEnquiries}
        actionLink={getWebEnquiryActionLink()}
        country={locale.country}
        filterByField="createdAt"
        filters={filters}
        filterStorageKey="intake-home-web-enquiries-filter"
        loading={loadingWebEnquiries || refetchingWebEnquiries}
        onFilterValueChanged={setWebEnquiriesTimeFilter}
        onClick={() => onWidgetClick("web-enquiries")}
      />
      <Widget
        iconName="regularSpinner"
        iconBgColour="bg-blue-160"
        title="Pending Intake Forms"
        submissions={pendingSubmissionsResult}
        actionLink={getPendingIntakeFormActionLink()}
        country={locale.country}
        filterStorageKey="intake-home-pending-intake-forms-filter"
        loading={loadingPendingSubmissions || refetchingPendingSubmissions}
        onClick={() => onWidgetClick("pending-forms")}
      />
      <Widget
        iconName="regularCheckCircle"
        iconBgColour="bg-green-200"
        title="Completed Intake Forms"
        submissions={completedSubmissions}
        actionLink={getCompletedIntakeFormActionLink()}
        country={locale.country}
        filterByField="completedAt"
        filters={filters}
        filterStorageKey="intake-home-completed-intake-forms-filter"
        loading={loadingCompletedSubmissions || refetchingCompletedSubmissions}
        onFilterValueChanged={setCompletedSubmissionsTimeFilter}
        onClick={() => onWidgetClick("completed-forms")}
      />
    </div>
  );
};
